import React from 'react'
import { pdf } from '@react-pdf/renderer'
import CertificatePDFGenerator from '../CertificatePDFGenerator/PDFTemplate'
import type {
  CertificatePDFData,
  ChemicalCompositionAdditionalRow,
  ChemicalCompositionRow,
  MechanicalTestsRow,
} from '../CertificatePDFGenerator/types'

export function getCertificateFieldValue(
  primary: unknown,
  ...fallbacks: unknown[]
): string {
  if (typeof primary === 'string' && primary.trim()) return primary
  for (const fallback of fallbacks) {
    if (typeof fallback === 'string' && fallback.trim()) return fallback
  }
  return ''
}

/**
 * Mesmo mapeamento usado em ViewCertificateModal (findById + getUtils).
 */
export function mapCertificateApiToPdfData(
  dataFromFindOne: any,
  dataFromUtilsView: any,
  idFallback: number,
): CertificatePDFData {
  const chemicalStructure = dataFromUtilsView.structures?.find(
    (s: any) => s.name === 'Composição química',
  )
  const mechanicalStructure = dataFromUtilsView.structures?.find(
    (s: any) => s.name === 'Ensaios mecânicos',
  )

  const chemicalCompositionValues: ChemicalCompositionRow &
    ChemicalCompositionAdditionalRow = {
    C: '',
    Mn: '',
    Cr: '',
    Cu: '',
    Fe: '',
    Nb: '',
    Ni: '',
    P: '',
    S: '',
    Si: '',
    Mo: '',
    Al: '',
    N: '',
    W: '',
    V: '',
    Co: '',
    Ti: '',
    B: '',
    Sn: '',
    Ta: '',
    O: '',
    H: '',
  }

  chemicalStructure?.columns?.forEach((col: any) => {
    const key = col.name as keyof typeof chemicalCompositionValues
    if (key in chemicalCompositionValues) {
      chemicalCompositionValues[key] = col.value || ''
    }
  })

  const mechanicalTestsValues: MechanicalTestsRow = {
    tensileStrengthValue: '',
    yieldStrengthValue: '',
    elongationValue: '',
    areaReductionValue: '',
    hardnessValue: '',
  }

  mechanicalStructure?.columns?.forEach((col: any) => {
    switch (col.name) {
      case 'Resistência':
        mechanicalTestsValues.tensileStrengthValue = col.value || ''
        break
      case 'Escoamento':
        mechanicalTestsValues.yieldStrengthValue = col.value || ''
        break
      case 'Alongamento':
        mechanicalTestsValues.elongationValue = col.value || ''
        break
      case 'Redução de área':
        mechanicalTestsValues.areaReductionValue = col.value || ''
        break
      case 'Dureza':
        mechanicalTestsValues.hardnessValue = col.value || ''
        break
      default:
        break
    }
  })

  return {
    id: dataFromFindOne.id || idFallback || 0,
    customer: getCertificateFieldValue(dataFromFindOne.customer?.companyName),
    supplier: getCertificateFieldValue(dataFromFindOne.supplier?.companyName),
    date: getCertificateFieldValue(dataFromUtilsView.date),
    heatNumber: getCertificateFieldValue(dataFromFindOne.heatNumber?.heatNumber),
    codeOfOP: getCertificateFieldValue(dataFromFindOne.codeOpString),
    fiscalNote: getCertificateFieldValue(dataFromFindOne.invoiceNumber),
    purchaseOrderNumber: getCertificateFieldValue(
      dataFromFindOne.purchaseOrderNumber,
    ),
    norms: getCertificateFieldValue(dataFromFindOne.standard),
    factory: getCertificateFieldValue(dataFromUtilsView.factory),
    heatTreatmentDetails: getCertificateFieldValue(
      dataFromFindOne.heatTreatment,
    ),
    description: getCertificateFieldValue(dataFromFindOne.description),
    startingMaterial: getCertificateFieldValue(
      dataFromFindOne.startingMaterial,
    ),

    chemicalComposition: {
      headersTop: [
        'C',
        'Mn',
        'Cr',
        'Cu',
        'Fe',
        'Nb',
        'Ni',
        'P',
        'S',
        'Si',
        'Mo',
      ],
      valuesTop: {
        C: chemicalCompositionValues.C,
        Mn: chemicalCompositionValues.Mn,
        Cr: chemicalCompositionValues.Cr,
        Cu: chemicalCompositionValues.Cu,
        Fe: chemicalCompositionValues.Fe,
        Nb: chemicalCompositionValues.Nb,
        Ni: chemicalCompositionValues.Ni,
        P: chemicalCompositionValues.P,
        S: chemicalCompositionValues.S,
        Si: chemicalCompositionValues.Si,
        Mo: chemicalCompositionValues.Mo,
      },
      headersBottom: ['Al', 'N', 'W', 'V', 'Co', 'Ti', 'B', 'Sn', 'Ta', 'O', 'H'],
      valuesBottom: {
        Al: chemicalCompositionValues.Al,
        N: chemicalCompositionValues.N,
        W: chemicalCompositionValues.W,
        V: chemicalCompositionValues.V,
        Co: chemicalCompositionValues.Co,
        Ti: chemicalCompositionValues.Ti,
        B: chemicalCompositionValues.B,
        Sn: chemicalCompositionValues.Sn,
        Ta: chemicalCompositionValues.Ta,
        O: chemicalCompositionValues.O,
        H: chemicalCompositionValues.H,
      },
    },
    mechanicalTests: {
      headers: {
        portuguese: [
          'Limite de Resistência',
          'Limite de Escoamento',
          'Alongamento',
          'Redução de Área',
          'Dureza',
        ],
        english: [
          'Tensile Strength',
          'Yield Strength',
          'Elongation',
          'Area Reduction',
          'Hardness',
        ],
      },
      values: mechanicalTestsValues,
    },
    qualityObservations: getCertificateFieldValue(dataFromFindOne.observations),
    responsibleName: getCertificateFieldValue(dataFromFindOne.responsibleName),
    responsibleSignatureDate: getCertificateFieldValue(
      dataFromFindOne.responsibleSignatureDate,
    ),
    additionalPages:
      dataFromFindOne.images
        ?.sort((a: any, b: any) => a.id - b.id)
        .map((img: any) => ({
          id: img.id,
          title: getCertificateFieldValue(img.title),
          subtitle: getCertificateFieldValue(img.subTitle),
          imageLink: getCertificateFieldValue(img.fileUrl),
        })) || [],
    param: dataFromFindOne.param,
    structures: dataFromUtilsView.structures,
  }
}

function sanitizeFileNamePart(value: string): string {
  return value.replace(/[^\w-]+/g, '_').slice(0, 80) || 'certificado'
}

/** Gera o mesmo PDF da tela de certificados e dispara o download no browser. */
export async function downloadCertificatePdfFromMappedData(
  data: CertificatePDFData,
): Promise<void> {
  const doc = React.createElement(CertificatePDFGenerator, {
    certificate: data,
  })
  const blob = await pdf(doc as never).toBlob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const customer = sanitizeFileNamePart(data.customer || '')
  a.download = `Certificado_${data.id}_${customer}.pdf`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
